import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  Stack,
  Chip,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  Fab,
  useMediaQuery,
} from '@mui/material';
import {
  Business,
  LocationOn,
  Phone,
  Email,
  Person,
  Verified,
  Map,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import PropertyMap from '../components/PropertyMap';
import photosPlaceholderSrc from '../assets/PhotosPlaceholder.png';
import { formatPropertyAddress, propertyImageAlt } from '../utils/propertyDisplay';

export default function VendorStorefront() {
  const { id } = useParams();
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [vendorData, setVendorData] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [mapOpen, setMapOpen] = useState(false);

  const normalizeStatus = (status) => {
    if (!status) return 'available';
    const statusMap = {
      'Available': 'available',
      'available': 'available',
      'On-Hold': 'on-hold',
      'on-hold': 'on-hold',
      'First Come First Serve': 'first come first serve',
      'first come first serve': 'first come first serve',
      'Vacant on Lockbox': 'vacant on lockbox',
      'vacant on lockbox': 'vacant on lockbox',
      'Under Contract': 'under contract',
      'under contract': 'under contract',
      'Pending': 'pending',
      'pending': 'pending',
      'Sold': 'sold',
      'sold': 'sold'
    };
    return statusMap[status] || String(status).trim().toLowerCase();
  };

  const getStatusColor = (status) => {
    const s = normalizeStatus(status);
    return (
      s === 'available' ? '#28a745' :
      s === 'on-hold' ? '#dc3545' :
      s === 'first come first serve' ? '#17a2b8' :
      s === 'vacant on lockbox' ? '#008080' :
      s === 'under contract' ? '#ffc107' :
      s === 'pending' ? '#6f42c1' :
      s === 'sold' ? '#fd7e14' :
      '#6c757d'
    );
  };

  const PLACEHOLDER_PHOTO_SRC = photosPlaceholderSrc;

  const formatMoney = (value) => {
    const n = typeof value === 'string' ? Number(value) : value;
    if (!Number.isFinite(n)) return 'Not Available';
    return `$${n.toLocaleString()}`;
  };

  const formatDate = (value) => {
    if (!value) return 'Not Available';
    const d = value?.seconds ? new Date(value.seconds * 1000) : new Date(value);
    if (Number.isNaN(d.getTime())) return 'Not Available';
    return d.toLocaleDateString();
  };

  const InfoRow = ({ label, value }) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1.5,
          width: '100%',
        }}
      >
        <Typography
          variant="body2"
          color="#4a5568"
          sx={{ fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          color="#4a5568"
          sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}
        >
          {value}
        </Typography>
      </Box>
  );

  // Handle map modal close with cleanup
  const handleMapClose = () => {
    setMapOpen(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        // Fetch vendor data
        const vendorResponse = await api.vendors.getById(id);
        console.log('[VendorStorefront] vendors.getById ->', id, vendorResponse?.data);
        // Ensure vendor data has required fields with defaults
        const vendorData = {
          name: 'Unknown Vendor',
          title: 'Real Estate Professional',
          company: 'Independent',
          location: 'Location not specified',
          phone: 'Phone not available',
          email: 'Email not available',
          bio: 'No bio available.',
          verified: false,
          memberSince: 'Recently',
          profilePhoto: null,
          ...vendorResponse.data
        };
        setVendorData(vendorData);

        // Fetch properties for this vendor
        const propertiesResponse = await api.properties.getPublicByVendor(id);
        console.log('[VendorStorefront] fetched properties:', propertiesResponse.data.length, 'for vendor', id);
        setProperties(propertiesResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoadError(error?.message || 'Failed to load vendor storefront');
        // If vendor not found, try to use the first vendor as fallback
        try {
          const fallbackResponse = await api.vendors.getById('vendor_001');
          // Ensure fallback vendor data has required fields with defaults
          const fallbackVendorData = {
            name: 'Unknown Vendor',
            title: 'Real Estate Professional',
            company: 'Independent',
            location: 'Location not specified',
            phone: 'Phone not available',
            email: 'Email not available',
            bio: 'No bio available.',
            verified: false,
            memberSince: 'Recently',
            profilePhoto: null,
            ...fallbackResponse.data
          };
          setVendorData(fallbackVendorData);
          const isOwnerViewingFallback = Boolean(userProfile?.uid && userProfile.uid === 'vendor_001');
          const fallbackPropertiesResponse = isOwnerViewingFallback
            ? await api.properties.getByVendor('vendor_001')
            : await api.properties.getPublicByVendor('vendor_001');
          setProperties(fallbackPropertiesResponse.data || []);
        } catch (fallbackError) {
          console.error('Fallback failed:', fallbackError);
          setVendorData({
            name: 'Vendor Not Found',
            title: 'Real Estate Professional',
            company: 'Independent',
            location: 'Location not specified',
            phone: 'Phone not available',
            email: 'Email not available',
            bio: 'This vendor profile could not be loaded.',
            verified: false,
            memberSince: 'Unknown',
            profilePhoto: null
          });
          setProperties([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, userProfile?.uid]);


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress sx={{ color: '#008080' }} />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f9fa', py: 4 }}>
        <Container maxWidth="md">
          <Alert severity="error">
            {loadError}
          </Alert>
        </Container>
      </Box>
    );
  }

  if (!vendorData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography variant="h6" color="text.secondary">Vendor not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* Header */}
      <Box sx={{ backgroundColor: '#008080', color: 'white', py: 4 }}>
        <Container maxWidth="xl">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  src={vendorData.profilePhoto}
                  sx={{
                    width: { xs: 88, sm: 104, md: 120 },
                    height: { xs: 88, sm: 104, md: 120 },
                    mx: 'auto',
                    mb: 2,
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    border: '4px solid white'
                  }}
                >
                  <Person sx={{ fontSize: 60 }} />
                </Avatar>
                {vendorData.verified && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                    <Verified sx={{ color: '#FFD700' }} />
                    <Typography variant="body2" sx={{ color: '#FFD700', fontWeight: 500 }}>
                      Verified Vendor
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={8}>
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' } }}
              >
                {vendorData.name}
              </Typography>
              <Typography variant="h5" sx={{ opacity: 0.9, mb: 2, fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' } }}>
                {vendorData.title}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.8, mb: 2, fontSize: { xs: '1rem', sm: '1.05rem', md: '1.25rem' } }}>
                {vendorData.company}
              </Typography>


              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <LocationOn sx={{ fontSize: 18 }} />
                <Typography variant="body1">{vendorData.location}</Typography>
              </Box>

            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 6 } }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            {/* Map Section */}
            {properties.length > 0 && (
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom sx={{ color: '#008080', display: 'flex', alignItems: 'center' }}>
                    <LocationOn sx={{ mr: 1 }} />
                    Property Locations
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Pan, zoom, and click markers to explore properties
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setMapOpen(true)}
                      sx={{
                        borderColor: '#008080',
                        color: '#008080',
                        '&:hover': {
                          borderColor: '#005252',
                          backgroundColor: 'rgba(0, 128, 128, 0.04)'
                        }
                      }}
                    >
                      View Full Map
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      height: { xs: 220, sm: 260, md: 300 },
                      border: '2px solid #008080',
                      borderRadius: 2,
                      overflow: 'hidden',
                      '&:hover': {
                        boxShadow: 4,
                        borderColor: '#005252'
                      }
                    }}
                  >
                    <PropertyMap
                      properties={properties}
                      compact={true}
                      initialFitMode="firstPropertyRadius"
                      initialRadiusMiles={30}
                      onViewProperty={(propertyId) => navigate(`/property/${propertyId}`)}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Properties Section */}
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom sx={{ color: '#008080', display: 'flex', alignItems: 'center' }}>
                  <Business sx={{ mr: 1 }} />
                  Available Properties
                </Typography>

                <Stack spacing={3}>
                  {properties && properties.length > 0 ? (
                    properties.map((property) => (
                    <Paper key={property.id} elevation={1} sx={{ p: 3 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={3}>
                          <Box
                            sx={{
                              height: { xs: 140, sm: 120 },
                              backgroundColor: 'grey.200',
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden'
                            }}
                          >
                            {(
                              (property.images && property.images.length > 0 && property.images[0]) ||
                              (property.externalImageUrls && property.externalImageUrls.length > 0 && property.externalImageUrls[0])
                            ) ? (
                              <Box
                                component="img"
                                src={
                                  (property.images && property.images.length > 0 && property.images[0]) ||
                                  (property.externalImageUrls && property.externalImageUrls.length > 0 && property.externalImageUrls[0])
                                }
                                alt={propertyImageAlt(property)}
                                onError={(e) => {
                                  const img = e.currentTarget;
                                  if (img?.src && img.src.includes('PhotosPlaceholder.png')) return;
                                  img.src = PLACEHOLDER_PHOTO_SRC;
                                }}
                                sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1 }}
                              />
                            ) : (
                              <Box
                                component="img"
                                src={PLACEHOLDER_PHOTO_SRC}
                                alt="Photos coming soon"
                                sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1 }}
                              />
                            )}
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={5} sx={{ minWidth: 0 }}>
                          <Typography variant="h6" gutterBottom>
                            {formatPropertyAddress(property)}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                            <Chip
                              label={
                                Number.isFinite(property.bedrooms) && property.bedrooms > 0
                                  ? `${property.bedrooms}BR`
                                  : 'BR: N/A'
                              }
                              size="small"
                              variant="outlined"
                              sx={{ maxWidth: '100%' }}
                            />
                            <Chip
                              label={
                                Number.isFinite(property.bathrooms) && property.bathrooms > 0
                                  ? `${property.bathrooms}BA`
                                  : 'BA: N/A'
                              }
                              size="small"
                              variant="outlined"
                              sx={{ maxWidth: '100%' }}
                            />
                            <Chip
                              label={
                                Number.isFinite(property.squareFootage) && property.squareFootage > 0
                                  ? `${property.squareFootage.toLocaleString()} sqft`
                                  : 'Sqft: N/A'
                              }
                              size="small"
                              variant="outlined"
                              sx={{ maxWidth: '100%' }}
                            />
                            <Chip
                              label={property.propertyType ? property.propertyType : 'Type: N/A'}
                              size="small"
                              variant="outlined"
                              sx={{ maxWidth: '100%' }}
                            />
                          </Box>
                          <Chip
                            label={property.status}
                            size="small"
                            sx={{
                              backgroundColor: getStatusColor(property.status),
                              color: 'white'
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4} sx={{ textAlign: 'right' }}>
                          <Typography variant="h6" color="#008080" gutterBottom>
                            ${property.price ? property.price.toLocaleString() : 'N/A'}
                          </Typography>
                          <Stack spacing={0.5} sx={{ alignItems: 'stretch' }}>
                            {property.hoaFee != null && (
                              <InfoRow
                                label="HOA"
                                value={`${formatMoney(property.hoaFee)}/mo`}
                              />
                            )}
                            {property.spaceRent != null && (
                              <InfoRow
                                label="Space Rent"
                                value={`${formatMoney(property.spaceRent)}/sq ft/yr`}
                              />
                            )}
                            {property.closeOfEscrow && (
                              <InfoRow
                                label="Close of Escrow"
                                value={formatDate(property.closeOfEscrow)}
                              />
                            )}
                          </Stack>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate(`/property/${property.id}`)}
                            sx={{
                              mt: 1,
                              borderColor: '#008080',
                              color: '#008080',
                              '&:hover': {
                                borderColor: '#005252',
                                backgroundColor: 'rgba(0, 128, 128, 0.04)'
                              }
                            }}
                            fullWidth={isMobile}
                          >
                            View Details
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      No properties available
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>

            {/* Contact Information */}
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: '#008080' }}>
                  Contact Information
                </Typography>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Phone sx={{ color: '#008080' }} />
                    <Typography variant="body1">{vendorData.phone}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Email sx={{ color: '#008080' }} />
                    <Typography variant="body1">{vendorData.email}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LocationOn sx={{ color: '#008080' }} />
                    <Typography variant="body1">{vendorData.location}</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* About Me */}
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: '#008080', display: 'flex', alignItems: 'center' }}>
                  <Person sx={{ mr: 1 }} />
                  About Me
                </Typography>
                <Typography variant="body1" sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {vendorData.bio}
                </Typography>
              </CardContent>
            </Card>

          </Grid>
        </Grid>
      </Container>

      {/* Map Dialog */}
      <Dialog
        open={mapOpen}
        onClose={handleMapClose}
        maxWidth="lg"
        fullWidth
        fullScreen
      >
        <PropertyMap
          properties={properties}
          onClose={handleMapClose}
          initialFitMode="firstPropertyRadius"
          initialRadiusMiles={30}
          onViewProperty={(propertyId) => navigate(`/property/${propertyId}`)}
        />
      </Dialog>

      {/* Floating Action Button for Map */}
      {properties.length > 0 && (
        <Fab
          color="primary"
          aria-label="view properties on map"
          onClick={() => setMapOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            backgroundColor: '#008080',
            '&:hover': {
              backgroundColor: '#005252'
            }
          }}
        >
          <Map />
        </Fab>
      )}
    </Box>
  );
}
